import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Avatar from '@mui/material/Avatar';
import { colorCodesForAvatar } from '../../../../constants/index';
import { chracterPosition } from '../../../../utils/index';
import HighlightText from '../HighlightText/HighlightText';
import { ReactComponent as DesktopIcon } from '../../../../assets/images/ChatImages/os-device.svg';
import { ReactComponent as MobileIcon } from '../../../../assets/images/mobile-devices.svg';

import { useNavigate } from 'react-router-dom';
import { LIVE_CHAT_HOME_PATH } from '../../../../config/featureFlags';

const headerSx = {
    fontWeight: 400,
    fontSize: '12px',
    color: '#405261',
    fontFamily: 'Inter',
    paddingBottom: '0px',
    paddingLeft: '0px',
    borderBottom: '1px solid #E5E9EC',
};

const cellSx = {
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 400,
    color: '#405261',
    borderBottom: '1px solid #E5E9EC',
    py: 0,
    paddingLeft: '0px',
    verticalAlign: 'middle',
};

const getLiveUrl = (chat) => {
    const base = `${LIVE_CHAT_HOME_PATH}?roomId=${chat._id}&fromSearch=1`;
    if (chat.is_archived) return `${base}&tab=archive`;
    if (chat.is_unAssigned) return `${base}&tab=unassigned`;
    return base;
};

const ChatsTable = ({ chats, searchQuery = '' }) => {
    const navigate = useNavigate();
    if (!chats || chats.length === 0) return null;

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        return date.toLocaleDateString();
    };

    return (
        <Box sx={{ marginBottom: '32px' }}>
            <Typography sx={{ color: '#061522', fontFamily: 'Inter', fontSize: '18px', fontWeight: 500, lineHeight: '18px', marginBottom: '5px' }}>
                Chats ({chats.length})
            </Typography>

            <TableContainer sx={{ boxShadow: 'none' }}>
                <Table sx={{ minWidth: 650, tableLayout: 'fixed' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...headerSx, width: '15%' }}>Customer</TableCell>
                            <TableCell sx={{ ...headerSx, width: '35%', maxWidth: 320 }}>Conversation</TableCell>
                            <TableCell sx={{ ...headerSx, width: '15%' }}>Assign to</TableCell>
                            <TableCell sx={{ ...headerSx, width: '25%' }}>Device</TableCell>
                            <TableCell sx={{ ...headerSx, width: '10%' }}>Last Updated</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {chats.map((chat) => {
                            const customerDisplay = chat.roomName || chat.latestMessage?.sender_name || 'Visitor';
                            const conversationText = chat.latestMessageContent|| 'No messages';
                            const assignee = chat.assigned_to_name || chat.assigned_to?.full_name || 'Unassigned';

                            return (
                                <TableRow
                                    key={chat._id}
                                    hover
                                    onClick={() => navigate(getLiveUrl(chat))}
                                    sx={{
                                        cursor: 'pointer',
                                        '& td': { height: '4rem', py: 0, verticalAlign: 'middle' },
                                    }}
                                >
                                    <TableCell sx={cellSx}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: colorCodesForAvatar[chracterPosition(customerDisplay.substring(0, 1).toUpperCase())],
                                                    width: 32,
                                                    height: 32,
                                                    fontSize: 14,
                                                    fontFamily: 'Inter',
                                                }}
                                            >
                                                {customerDisplay.substring(0, 1).toUpperCase()}
                                            </Avatar>
                                            <HighlightText
                                                text={customerDisplay}
                                                query={searchQuery}
                                                sx={{ color: '#405261', fontFamily: 'Inter', fontSize: '14px', fontWeight: 400 }}
                                            />
                                        </Box>
                                    </TableCell>

                                    <TableCell sx={{ ...cellSx, maxWidth: 320, width: '35%' }}>
                                        <HighlightText
                                            text={conversationText}
                                            query={searchQuery}
                                            sx={{
                                                color: '#061522',
                                                fontFamily: 'Inter',
                                                fontSize: '14px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: 320,
                                                display: 'block',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={cellSx}>
                                        <HighlightText text={assignee} query={searchQuery} sx={{ color: '#061522', fontFamily: 'Inter', fontSize: '14px' }} />
                                    </TableCell>

                                    <TableCell sx={cellSx}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            {chat.device === 'Desktop' ? <DesktopIcon /> : <MobileIcon />}
                                            <span>{chat.device || 'Web'}</span>
                                        </div>
                                    </TableCell>

                                    <TableCell sx={cellSx}>{formatTimestamp(chat.updatedAt)}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ChatsTable;
